import { ref, onMounted, onActivated, onUnmounted } from 'vue'

/**
 * 动图重播 composable
 *
 * 每次组件挂载/激活时，通过 blob: URL 让动图从第一帧播放。
 *
 * - **KeepAlive 兼容**：同时绑定 onMounted + onActivated
 * - **跨域降级**：fetch 失败自动 fallback 时间戳方案
 * - **无坏图标**：初始直接显示原始图，fetch 完成后静默切换
 *
 * @param src 图片 URL
 * @returns imageSrc - 绑定到 <img :src>
 */
export function useAnimatedImage(src: string) {
  const imageSrc = ref(src)
  let objectUrl: string | null = null
  let pending = false
  let unmounted = false

  async function restart() {
    if (pending || unmounted) return
    pending = true

    if (objectUrl) {
      URL.revokeObjectURL(objectUrl)
      objectUrl = null
    }

    imageSrc.value = src

    try {
      const res = await fetch(src)
      const blob = await res.blob()
      if (unmounted) return // 已卸载，不再操作
      objectUrl = URL.createObjectURL(blob)
      imageSrc.value = objectUrl
    } catch {
      // 跨域/neterr → 时间戳降级
      imageSrc.value = `${src}?t=${Date.now()}`
    } finally {
      pending = false
    }
  }

  onMounted(restart)
  onActivated(restart)

  onUnmounted(() => {
    unmounted = true
    if (objectUrl) URL.revokeObjectURL(objectUrl)
  })

  return { imageSrc }
}
